import { useEffect, useState } from 'react';
import L from 'leaflet';

/**
 * Returns `true` once the Leaflet map's container has a real (non-zero) size.
 *
 * react-leaflet initializes the Leaflet map during React's commit phase, before
 * CSS flex layout has propagated. Leaflet reads the container as 0×0, caches
 * bad bounds, and any work dependent on those bounds (marker clustering,
 * flyToBounds, projection math) runs against bogus state.
 *
 * This hook watches the container with a ResizeObserver and calls
 * `map.invalidateSize()` on every real resize so Leaflet always matches the
 * DOM. Consumers should gate their own effects on the returned ready flag.
 */
export const useMapReady = (map: L.Map): boolean => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const container = map.getContainer();

    const check = () => {
      const { width, height } = container.getBoundingClientRect();
      if (width > 0 && height > 0) {
        map.invalidateSize(false);
        setReady(true);
      }
    };

    check();
    const observer = new ResizeObserver(check);
    observer.observe(container);
    return () => observer.disconnect();
  }, [map]);

  return ready;
};
